import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Gauge } from "lucide-react"
import { Slider } from "@/components/ui/slider"
import { type AIMonitorConfig } from "@/types/config"

interface ThresholdSettingsProps {
  thresholds: AIMonitorConfig['Thresholds']
  onUpdate: (config: Partial<AIMonitorConfig>) => void
  isLoading?: boolean
}

export function ThresholdSettings({ thresholds, onUpdate, isLoading }: ThresholdSettingsProps) {
  const updateThreshold = (key: keyof AIMonitorConfig['Thresholds'], value: number) => {
    onUpdate({
      Thresholds: {
        ...thresholds,
        [key]: value
      }
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-blue-500" />
          Impact Score Thresholds
        </CardTitle>
        <CardDescription>
          Bills exceeding any threshold will be monitored
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <label className="text-sm font-medium">Overall Impact</label>
            <span className="text-sm text-muted-foreground">{thresholds.overall}</span>
          </div>
          <Slider
            value={[thresholds.overall]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val: number[]) => updateThreshold('overall', val[0])}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <label className="text-sm font-medium">Direct UMCP Impact</label>
            <span className="text-sm text-muted-foreground">{thresholds.direct_umcp}</span>
          </div>
          <Slider
            value={[thresholds.direct_umcp]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val: number[]) => updateThreshold('direct_umcp', val[0])}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <label className="text-sm font-medium">Indirect UMCP Impact</label>
            <span className="text-sm text-muted-foreground">{thresholds.indirect_umcp}</span>
          </div>
          <Slider
            value={[thresholds.indirect_umcp]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val: number[]) => updateThreshold('indirect_umcp', val[0])}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <label className="text-sm font-medium">Direct Higher Ed Impact</label>
            <span className="text-sm text-muted-foreground">{thresholds.direct_higher_ed}</span>
          </div>
          <Slider
            value={[thresholds.direct_higher_ed]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val: number[]) => updateThreshold('direct_higher_ed', val[0])}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <label className="text-sm font-medium">Indirect Higher Ed Impact</label>
            <span className="text-sm text-muted-foreground">{thresholds.indirect_higher_ed}</span>
          </div>
          <Slider
            value={[thresholds.indirect_higher_ed]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val: number[]) => updateThreshold('indirect_higher_ed', val[0])}
            disabled={isLoading}
          />
        </div>
      </CardContent>
    </Card>
  )
}